import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch, ApiError, type User } from "@/api/client"
import type { components } from "@/api/schema"

type LoginRequest = components["schemas"]["LoginRequest"]
type RegisterRequest = components["schemas"]["RegisterRequest"]
type ChangePasswordRequest = components["schemas"]["ChangePasswordRequest"]

interface ChangePasswordResponse {
  success: boolean
  message: string
}

export const AUTH_ME_QUERY_KEY = ["auth", "me"] as const

/**
 * `/auth/me` probe used by `<AuthProvider/>`. Returns the current User on
 * success and `null` on 401 (unauthenticated). Anything else (500, network
 * error) bubbles as an exception so it surfaces in an error boundary — we
 * don't want to silently treat a flaky API as "logged out" and bounce
 * every visitor to /login.
 */
export function useCurrentUser() {
  return useQuery<User | null>({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async () => {
      try {
        return await apiFetch<User>("/auth/me")
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null
        throw err
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useLogin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: LoginRequest) =>
      apiFetch<unknown>("/auth/login", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    },
  })
}

export function useRegister() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: RegisterRequest) =>
      apiFetch<unknown>("/auth/register", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
    },
  })
}

/**
 * `/auth/change-password` mutation used by the profile page (T3.2).
 * Does NOT invalidate `AUTH_ME_QUERY_KEY` — the `UserRead` payload does
 * not include the password and a password change does not affect any
 * field the app reads from `/auth/me`, so a refetch would be wasted.
 */
export function useChangePassword() {
  return useMutation<ChangePasswordResponse, Error, ChangePasswordRequest>({
    mutationFn: (body) =>
      apiFetch<ChangePasswordResponse>("/auth/change-password", {
        method: "POST",
        body,
      }),
  })
}

export function useLogout() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<unknown>("/auth/logout", { method: "POST" }),
    onSuccess: () => {
      qc.setQueryData(AUTH_ME_QUERY_KEY, null)
    },
  })
}
